import { CalendarX2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { InviteDialog } from "@/components/dashboard/InviteDialog";
import { MeetingCard, MeetingCardSkeleton } from "@/components/dashboard/MeetingCard";
import { QuickStart } from "@/components/dashboard/QuickStart";
import { ScheduleDialog } from "@/components/dashboard/ScheduleDialog";
import { AppShell } from "@/components/layout/AppShell";
import { Seo } from "@/components/Seo";
import { Card, CardHeader } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { useSession } from "@/context/SessionContext";
import { siteOrigin } from "@/env";
import { cancelMeeting, listMeetings } from "@/lib/api/meetings";
import type { Meeting } from "@/lib/api/types";
import { useAsync } from "@/lib/useAsync";
import { useT } from "@/lib/i18n";

export function DashboardPage() {
  const { user } = useSession();
  const toast = useToast();
  const t = useT();
  const [scheduling, setScheduling] = useState(false);
  const [editing, setEditing] = useState<Meeting | null>(null);
  const [inviting, setInviting] = useState<Meeting | null>(null);
  const [cancelling, setCancelling] = useState<Meeting | null>(null);
  const [overrides, setOverrides] = useState<Record<string, Meeting>>({});

  const { data, loading, error, reload } = useAsync(() => listMeetings(), []);

  const meetings = useMemo(() => {
    const list = (data?.items ?? []).map((meeting) => overrides[meeting.id] ?? meeting);
    return list.filter((meeting) => meeting.status !== "cancelled");
  }, [data, overrides]);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
    }, 60_000);
    return () => {
      clearInterval(id);
    };
  }, []);

  const upcoming = meetings.filter((meeting) => new Date(meeting.endsAt).getTime() >= now);
  const past = meetings.filter((meeting) => new Date(meeting.endsAt).getTime() < now);

  const saved = (meeting: Meeting) => {
    setOverrides((current) => ({ ...current, [meeting.id]: meeting }));
    reload();
  };

  const copyLink = (meeting: Meeting) => {
    const link = `${siteOrigin()}/${meeting.roomSlug}`;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.show(t("meeting.linkCopied"), "success");
      })
      .catch(() => {
        toast.show(link, "info");
      });
  };

  const cancel = async (meeting: Meeting) => {
    try {
      await cancelMeeting(meeting.id);
      setOverrides((current) => ({
        ...current,
        [meeting.id]: { ...meeting, status: "cancelled" },
      }));
      setCancelling(null);
      toast.show(t("schedule.cancelled"), "success");
    } catch (error_) {
      setCancelling(null);
      toast.fail(error_);
    }
  };

  const card = (meeting: Meeting) => (
    <MeetingCard
      isOrganizer={meeting.createdBy === user?.id}
      key={meeting.id}
      meeting={meeting}
      onCancel={() => {
        setCancelling(meeting);
      }}
      onCopyLink={() => {
        copyLink(meeting);
      }}
      onEdit={() => {
        setEditing(meeting);
      }}
      onInvite={() => {
        setInviting(meeting);
      }}
    />
  );

  return (
    <AppShell>
      <Seo
        description="Upcoming and scheduled meetings."
        noindex
        path="/dashboard"
        title="Your meetings"
      />
      <div className="flex flex-col gap-6">
        <header>
          <h1 className="text-2xl font-semibold text-ink">
            {user === null
              ? t(greetingKey())
              : t("dash.greeting", {
                  greeting: t(greetingKey()),
                  name: user.displayName.split(" ")[0] ?? user.displayName,
                })}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">{t("dash.subtitle")}</p>
        </header>

        <QuickStart
          onSchedule={() => {
            setScheduling(true);
          }}
        />

        {}
        {loading && data === null ? (
          <Card>
            <CardHeader title={t("dash.upcoming")} />
            <SkeletonList className="flex flex-col gap-3 p-4" label={t("dash.loadingMeetings")}>
              <MeetingCardSkeleton />
              <MeetingCardSkeleton />
              <MeetingCardSkeleton />
            </SkeletonList>
          </Card>
        ) : error !== null ? (
          <Card>
            <EmptyState
              description={t("dash.loadFailed")}
              icon={CalendarX2}
              title={t("common.somethingWentWrong")}
            />
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader
                description={
                  upcoming.length === 0
                    ? undefined
                    : t("dash.scheduledCount", { count: upcoming.length })
                }
                title={t("dash.upcoming")}
              />
              {upcoming.length === 0 ? (
                <EmptyState
                  description={t("dash.nothingScheduledBody")}
                  icon={CalendarX2}
                  title={t("dash.nothingScheduled")}
                />
              ) : (
                <div className="flex flex-col gap-3 p-4">{upcoming.map(card)}</div>
              )}
            </Card>

            {past.length > 0 ? (
              <Card>
                <CardHeader title={t("dash.past")} />
                <div className="flex flex-col gap-3 p-4 opacity-75">
                  {past.slice(0, 10).map(card)}
                </div>
              </Card>
            ) : null}
          </>
        )}
      </div>

      {scheduling ? (
        <ScheduleDialog
          onClose={() => {
            setScheduling(false);
          }}
          onSaved={saved}
        />
      ) : null}

      {editing !== null ? (
        <ScheduleDialog
          meeting={editing}
          onClose={() => {
            setEditing(null);
          }}
          onSaved={saved}
        />
      ) : null}

      {inviting !== null ? (
        <InviteDialog
          meeting={inviting}
          onChanged={saved}
          onClose={() => {
            setInviting(null);
          }}
        />
      ) : null}

      {cancelling !== null ? (
        <ConfirmDialog
          cancelLabel={t("schedule.cancelKeep")}
          confirmLabel={t("schedule.cancelAction")}
          destructive
          message={t("schedule.cancelBody")}
          onCancel={() => {
            setCancelling(null);
          }}
          onConfirm={() => cancel(cancelling)}
          title={t("schedule.cancelTitle", { title: cancelling.title })}
        />
      ) : null}
    </AppShell>
  );
}

function greetingKey(): "dash.goodMorning" | "dash.goodAfternoon" | "dash.goodEvening" {
  const hour = new Date().getHours();
  if (hour < 12) return "dash.goodMorning";
  if (hour < 18) return "dash.goodAfternoon";
  return "dash.goodEvening";
}
